import {
  DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
  DEFAULT_WORKFLOW_CHART_TYPE,
  getIsWorkflowChartDateBucket,
  getIsWorkflowChartDateDimension,
  getIsWorkflowChartDimension,
  getIsWorkflowChartMeasure,
  getIsWorkflowChartType,
  getIsWorkflowListGroupableDimension,
  getIsWorkflowSystemChartPresetId,
  getResolvedWorkflowChartGrain,
  getWorkflowSystemChartPreset,
  MAX_WORKFLOW_CHART_DIMENSIONS,
  parseWorkflowViewState,
  WORKFLOW_CHART_MEASURES,
} from '@aline/common';
import type {
  WorkflowChartDateBucket,
  WorkflowChartDimension,
  WorkflowChartGrain,
  WorkflowChartMeasure,
  WorkflowChartSeriesValue,
  WorkflowChartType,
  WorkflowViewState,
} from '@aline/common';
import { isEqual } from 'lodash';
import type { Apollo } from 'src/gql/generated';

export const WORKFLOW_LIST_CHART_ID = 'main';

/** Fallback Chart heading, shown until the user names the Chart. */
export const UNTITLED_WORKFLOW_LIST_CHART_TITLE = 'Untitled chart';

/** Charts-panel section titles. MUI's defaults are Categories / Series. */
export const WORKFLOW_LIST_CHARTS_VALUES_LABEL = 'Measure';
export const WORKFLOW_LIST_CHARTS_DIMENSIONS_LABEL = 'Categories';

export function getIsWorkflowListChartableField(field: string): boolean {
  return getIsWorkflowChartDimension(field) || getIsWorkflowChartMeasure(field);
}

/**
 * MUI offers "Add to Measure" for any unused field once the grid is grouped.
 * Insights only measures Count and Typical time, so the panel menu has to hide
 * the section moves the server-series contract cannot render.
 */
export function getIsWorkflowListChartsSectionMenuItemAllowed(options: {
  addToDimensionsLabel: string;
  addToValuesLabel: string;
  field: string;
  menuItemLabel: string;
}): boolean {
  const { addToDimensionsLabel, addToValuesLabel, field, menuItemLabel } = options;
  if (menuItemLabel === addToValuesLabel) return getIsWorkflowChartMeasure(field);
  if (menuItemLabel === addToDimensionsLabel) return getIsWorkflowChartDimension(field);
  return true;
}

export type WorkflowListChartFieldItem = {
  field: string;
  hidden?: boolean;
};

export type WorkflowListChartConfig = {
  readonly chartType?: WorkflowChartType;
  readonly configuration?: Record<string, string | number | boolean | null>;
  readonly dateBucket?: WorkflowChartDateBucket;
  readonly dimensions?: WorkflowListChartFieldItem[];
  readonly grain?: WorkflowChartGrain;
  readonly insightValues?: WorkflowChartSeriesValue[];
  readonly minSampleSize?: number;
  readonly title?: string;
  readonly values?: WorkflowListChartFieldItem[];
};

export type WorkflowListChartsIntegrationSlice = {
  activeChartId?: string;
  charts?: Record<string, WorkflowListChartConfig>;
};

/**
 * A Chart with no Dimensions chosen yet. Every entry point that seeds a Chart —
 * the grid's initial state, a new View, the builder's "add chart" — starts here so
 * the unconfigured shape stays in one place.
 */
export function createDefaultWorkflowListChartConfig(
  overrides: WorkflowListChartConfig = {}
): WorkflowListChartConfig {
  return {
    chartType: DEFAULT_WORKFLOW_CHART_TYPE,
    dimensions: [],
    values: createDefaultWorkflowListChartValues(),
    ...overrides,
  };
}

function createDefaultWorkflowListChartValues(): WorkflowListChartFieldItem[] {
  return [{ field: WORKFLOW_CHART_MEASURES.count }];
}

function getVisibleChartDimensionItems(
  items: WorkflowListChartFieldItem[] | undefined
): WorkflowListChartFieldItem[] {
  const unique: WorkflowListChartFieldItem[] = [];
  const seen = new Set<string>();
  for (const item of items ?? []) {
    if (item.hidden || seen.has(item.field) || !getIsWorkflowChartDimension(item.field)) continue;
    seen.add(item.field);
    unique.push({ field: item.field });
  }
  return unique;
}

/**
 * Insights charts one primary Category (axis) and at most one secondary (stack).
 * MUI's Fields panel will accept a third, so treat a newly added Category as a
 * replacement of the secondary instead of growing a list the series contract
 * cannot render.
 */
export function getWorkflowListChartDimensionItems(
  items: WorkflowListChartFieldItem[] | undefined,
  previousItems?: WorkflowListChartFieldItem[]
): WorkflowListChartFieldItem[] {
  const unique = getVisibleChartDimensionItems(items);
  if (unique.length <= MAX_WORKFLOW_CHART_DIMENSIONS) return unique;

  const previous = getVisibleChartDimensionItems(previousItems);
  const previousFields = new Set(previous.map(item => item.field));
  const added = unique.find(item => !previousFields.has(item.field));
  const primary = previous.at(0) ?? unique.at(0);
  if (!primary) return [];
  if (added && added.field !== primary.field)
    return [{ field: primary.field }, { field: added.field }];

  return unique.slice(0, MAX_WORKFLOW_CHART_DIMENSIONS);
}

export function getWorkflowListChartDimensions(
  items: WorkflowListChartFieldItem[] | undefined,
  previousItems?: WorkflowListChartFieldItem[]
): WorkflowChartDimension[] {
  return getWorkflowListChartDimensionItems(items, previousItems)
    .map(item => item.field)
    .filter(getIsWorkflowChartDimension);
}

export function getWorkflowListGroupFieldsFromChartDimensions(
  dimensions: WorkflowChartDimension[]
): string[] {
  return dimensions
    .filter(getIsWorkflowListGroupableDimension)
    .slice(0, MAX_WORKFLOW_CHART_DIMENSIONS);
}

/** Takes a raw persisted chart too, where `chartType` is an unvalidated string. */
export function getWorkflowListChartType(
  chart: { chartType?: string } | undefined
): WorkflowChartType {
  if (chart?.chartType && getIsWorkflowChartType(chart.chartType)) return chart.chartType;
  return DEFAULT_WORKFLOW_CHART_TYPE;
}

export function getWorkflowListChartMeasure(
  items: WorkflowListChartFieldItem[] | undefined
): WorkflowChartMeasure {
  const field = items?.find(item => !item.hidden && getIsWorkflowChartMeasure(item.field))?.field;
  if (field && getIsWorkflowChartMeasure(field)) return field;
  return WORKFLOW_CHART_MEASURES.count;
}

/**
 * Workflow Insights queries one Measure at a time. MUI allows several fields in
 * its Series section, so treat a newly added Measure as a replacement instead
 * of retaining a list the server-series contract cannot render.
 */
export function getWorkflowListSingleChartValue(
  items: WorkflowListChartFieldItem[] | undefined,
  previousItems?: WorkflowListChartFieldItem[]
): WorkflowListChartFieldItem[] {
  const visibleMeasures = (items ?? []).filter(
    item => !item.hidden && getIsWorkflowChartMeasure(item.field)
  );
  const previousFields = new Set((previousItems ?? []).map(item => item.field));
  const addedMeasure = visibleMeasures.find(item => !previousFields.has(item.field));
  const selectedMeasure = addedMeasure ?? visibleMeasures[0];

  return [{ field: selectedMeasure?.field ?? WORKFLOW_CHART_MEASURES.count }];
}

export function getWorkflowListChartDateBucket(
  chart: WorkflowListChartConfig | undefined,
  dimensions: WorkflowChartDimension[]
): WorkflowChartDateBucket | undefined {
  if (!dimensions.some(getIsWorkflowChartDateDimension)) return undefined;
  if (chart?.dateBucket && getIsWorkflowChartDateBucket(chart.dateBucket)) return chart.dateBucket;
  return DEFAULT_WORKFLOW_CHART_DATE_BUCKET;
}

export function getWorkflowListSeriesInput(options: {
  dimensions: WorkflowChartDimension[];
  filterModel: unknown;
  measure: WorkflowChartMeasure;
  workflowFilter: Apollo.WorkflowFilterInput;
  dateBucket?: WorkflowChartDateBucket;
  grain?: WorkflowChartGrain;
  insightValues?: WorkflowChartSeriesValue[];
  minSampleSize?: number;
}): Apollo.WorkflowChartSeriesInput | null {
  const {
    dateBucket,
    dimensions,
    filterModel,
    grain,
    insightValues,
    measure,
    minSampleSize,
    workflowFilter,
  } = options;
  if (!dimensions.length) return null;
  return {
    dateBucket: dateBucket ?? null,
    dimensions: [...dimensions],
    filterModel: filterModel ?? null,
    grain: getResolvedWorkflowChartGrain({
      completed: workflowFilter.completed,
      dimensions,
      filterModel,
      grain,
      measure,
    }),
    measure,
    minSampleSize: minSampleSize ?? null,
    values: insightValues ?? null,
    workflowFilter,
  };
}

/**
 * Persistable Workflow View slice from the Workflows list — never stores series data.
 */
export function getWorkflowViewStateFromWorkflowList(options: {
  filterModel: unknown;
  rowGroupingModel: readonly string[];
  sortModel: ReadonlyArray<{ field: string; sort?: 'asc' | 'desc' | null }>;
  aggregationModel?: Record<string, string> | null;
  chartsIntegration?: WorkflowListChartsIntegrationSlice | null;
  workflowFilter?: WorkflowViewState['workflowFilter'];
}): WorkflowViewState {
  const {
    aggregationModel,
    chartsIntegration,
    filterModel,
    rowGroupingModel,
    sortModel,
    workflowFilter,
  } = options;
  const groupingModel = rowGroupingModel
    .filter(getIsWorkflowListGroupableDimension)
    .slice(0, MAX_WORKFLOW_CHART_DIMENSIONS);

  return parseWorkflowViewState({
    aggregation: aggregationModel ? { model: aggregationModel } : undefined,
    chartsIntegration: chartsIntegration
      ? {
          activeChartId: chartsIntegration.activeChartId,
          charts: chartsIntegration.charts,
        }
      : undefined,
    filter: { filterModel },
    rowGrouping: groupingModel.length ? { model: [...groupingModel] } : undefined,
    sorting: { sortModel: [...sortModel] },
    workflowFilter,
  });
}

export function getWorkflowListChartIdForPin(state: WorkflowViewState): string | null {
  const charts = state.chartsIntegration?.charts;
  if (!charts) return null;
  const preferredId = state.chartsIntegration?.activeChartId;
  if (preferredId && charts[preferredId]) {
    const dimensions = getWorkflowListChartDimensions(
      normalizeChartFieldItems(charts[preferredId]?.dimensions)
    );
    if (dimensions.length) return preferredId;
  }

  for (const [chartId, chart] of Object.entries(charts)) {
    const dimensions = getWorkflowListChartDimensions(normalizeChartFieldItems(chart.dimensions));
    if (dimensions.length) return chartId;
  }
  return null;
}

export function getAppliedWorkflowListStateFromWorkflowView(state: unknown): {
  aggregationModel: Record<string, string>;
  chartsIntegration: WorkflowListChartsIntegrationSlice;
  filterModel: unknown;
  rowGroupingModel: string[];
  sortModel: Array<{ field: string; sort?: 'asc' | 'desc' | null }>;
  workflowFilter?: WorkflowViewState['workflowFilter'];
} {
  const parsed = parseWorkflowViewState(state);
  const rowGroupingModel = (parsed.rowGrouping?.model ?? []).filter(
    getIsWorkflowListGroupableDimension
  );
  const sortModel = parsed.sorting?.sortModel ?? [];
  const rawCharts = parsed.chartsIntegration?.charts;
  const charts: WorkflowListChartsIntegrationSlice['charts'] = rawCharts
    ? Object.fromEntries(
        Object.entries(rawCharts).map(([chartId, chart]) => [
          chartId,
          {
            chartType: getWorkflowListChartType(chart),
            configuration: chart.configuration,
            dateBucket: chart.dateBucket,
            dimensions: getWorkflowListChartDimensionItems(
              normalizeChartFieldItems(chart.dimensions)
            ),
            grain: chart.grain,
            insightValues: chart.insightValues,
            minSampleSize: chart.minSampleSize,
            title: chart.title,
            values: normalizeChartFieldItems(chart.values),
          },
        ])
      )
    : { [WORKFLOW_LIST_CHART_ID]: createDefaultWorkflowListChartConfig() };

  const chartMeasure = getWorkflowListChartMeasure(
    charts?.[parsed.chartsIntegration?.activeChartId ?? WORKFLOW_LIST_CHART_ID]?.values
  );
  const aggregationModel = parsed.aggregation?.model ?? { [chartMeasure]: chartMeasure };

  return {
    aggregationModel,
    chartsIntegration: {
      activeChartId: parsed.chartsIntegration?.activeChartId ?? WORKFLOW_LIST_CHART_ID,
      charts,
    },
    filterModel: parsed.filter?.filterModel ?? { items: [] },
    rowGroupingModel,
    sortModel,
    workflowFilter: parsed.workflowFilter,
  };
}

/**
 * Restore every chart on the View (multi-chart), then activate the saved activeChartId.
 */
export function applyChartsIntegrationToGrid(
  gridApi: {
    setActiveChartId: (chartId: string) => void;
    setChartType: (chartId: string, type: string) => void;
    updateChartDimensionsData: (
      chartId: string,
      dimensions: Array<{ field: string; hidden?: boolean }>
    ) => void;
    updateChartValuesData: (
      chartId: string,
      values: Array<{ field: string; hidden?: boolean }>
    ) => void;
  },
  chartsIntegration: WorkflowListChartsIntegrationSlice
): void {
  const charts = chartsIntegration.charts ?? {};
  const chartIds = Object.keys(charts);
  if (!chartIds.length) {
    const fallbackId = chartsIntegration.activeChartId ?? WORKFLOW_LIST_CHART_ID;
    gridApi.setChartType(fallbackId, DEFAULT_WORKFLOW_CHART_TYPE);
    gridApi.updateChartValuesData(fallbackId, createDefaultWorkflowListChartValues());
    gridApi.updateChartDimensionsData(fallbackId, []);
    gridApi.setActiveChartId(fallbackId);
    return;
  }

  for (const [chartId, chart] of Object.entries(charts)) {
    gridApi.setChartType(chartId, getWorkflowListChartType(chart));
    gridApi.updateChartDimensionsData(
      chartId,
      getWorkflowListChartDimensionItems(chart.dimensions)
    );
    gridApi.updateChartValuesData(
      chartId,
      (chart.values ?? createDefaultWorkflowListChartValues()).map(item => ({
        field: item.field,
        hidden: item.hidden,
      }))
    );
  }

  const firstChartId = chartIds.at(0);
  const activeChartId =
    chartsIntegration.activeChartId && charts[chartsIntegration.activeChartId]
      ? chartsIntegration.activeChartId
      : firstChartId;
  if (!activeChartId) return;

  gridApi.setActiveChartId(activeChartId);
}

/**
 * Seeds a new Chart on the grid and makes it active. A brand-new chart id has no
 * entry in the grid's chartsIntegration state yet, so these updates both create
 * and populate it. The chart type is left to the renderer proxy to backfill once
 * the chart registers — `setChartType` no-ops for unregistered ids.
 */
export function addWorkflowListChart(gridApi: {
  setActiveChartId: (chartId: string) => void;
  state: { chartsIntegration: { charts: Record<string, unknown> } };
  updateChartDimensionsData: (
    chartId: string,
    dimensions: Array<{ field: string; hidden?: boolean }>
  ) => void;
  updateChartValuesData: (
    chartId: string,
    values: Array<{ field: string; hidden?: boolean }>
  ) => void;
}): string {
  const chartId = getNextWorkflowListChartId(Object.keys(gridApi.state.chartsIntegration.charts));
  gridApi.updateChartValuesData(chartId, createDefaultWorkflowListChartValues());
  gridApi.updateChartDimensionsData(chartId, []);
  gridApi.setActiveChartId(chartId);
  return chartId;
}

/**
 * Human label for a Chart — used by the Charts panel's chart selector.
 * Drill-down-created charts are keyed by their System Chart Preset id, so those get
 * the preset name instead of the raw id.
 */
export function getWorkflowListChartLabel(
  chartId: string,
  chart?: WorkflowListChartConfig
): string {
  if (chart?.title) return chart.title;
  if (chartId === WORKFLOW_LIST_CHART_ID) return 'Chart 1';
  if (getIsWorkflowSystemChartPresetId(chartId)) return getWorkflowSystemChartPreset(chartId).name;
  const numbered = /^chart-(\d+)$/.exec(chartId);
  if (numbered) return `Chart ${numbered[1]}`;
  return chartId;
}

export function getNextWorkflowListChartId(
  existingIds: readonly string[],
  prefix = 'chart'
): string {
  let index = existingIds.length + 1;
  let candidate = `${prefix}-${index}`;
  const existing = new Set(existingIds);
  while (existing.has(candidate)) {
    index += 1;
    candidate = `${prefix}-${index}`;
  }
  return candidate;
}

export function getWorkflowListChartIdsForSync(options: {
  gridChartIds: readonly string[];
  ignoredChartIds: readonly string[];
  controlledChartIds?: readonly string[];
}): string[] {
  const ignoredChartIds = new Set(options.ignoredChartIds);
  if (options.controlledChartIds) return [...options.controlledChartIds];
  return options.gridChartIds.filter(chartId => !ignoredChartIds.has(chartId));
}

/**
 * Tab panes re-run one-way grid syncs on every re-show. Skip when the slice
 * already matches what we last wrote, including a structurally equal copy.
 */
export function pushChartsIntegrationIfChanged(options: {
  apply: (slice: WorkflowListChartsIntegrationSlice) => void;
  lastPushed: WorkflowListChartsIntegrationSlice | null;
  next: WorkflowListChartsIntegrationSlice;
}): WorkflowListChartsIntegrationSlice {
  if (isEqual(options.lastPushed, options.next)) return options.lastPushed ?? options.next;
  options.apply(options.next);
  return options.next;
}

export const WORKFLOW_LIST_CHART_PROXY_SYNC = {
  adoptPanel: 'adopt-panel',
  backfill: 'backfill',
  skip: 'skip',
} as const;

/**
 * The Charts-integration context survives tab hide, but this effect still
 * re-runs. Only write when the registered type is missing or disagrees.
 */
export function getWorkflowListChartProxySyncAction(options: {
  desiredType: WorkflowChartType;
  registeredType: string | undefined;
}): (typeof WORKFLOW_LIST_CHART_PROXY_SYNC)[keyof typeof WORKFLOW_LIST_CHART_PROXY_SYNC] {
  const { desiredType, registeredType } = options;
  if (registeredType === undefined || registeredType === desiredType)
    return WORKFLOW_LIST_CHART_PROXY_SYNC.skip;
  if (!registeredType) return WORKFLOW_LIST_CHART_PROXY_SYNC.backfill;
  return WORKFLOW_LIST_CHART_PROXY_SYNC.adoptPanel;
}

/** Grouping already paints the title via `leafField: 'name'`. */
export function getShouldSetWorkflowListNameColumnVisibility(options: {
  isGrouped: boolean;
  isNameVisible: boolean;
}): boolean {
  return options.isNameVisible !== !options.isGrouped;
}

function normalizeChartFieldItems(
  items: Array<string | WorkflowListChartFieldItem> | undefined
): WorkflowListChartFieldItem[] {
  if (!items?.length) return [];
  return items.map(item => (typeof item === 'string' ? { field: item } : item));
}
